import React, { useState, useCallback } from "react";

// Simple Tag component for triggers
const TriggerTag = ({ trigger, onRemove }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 mr-1 mb-1">
    {trigger}
    <button
      type="button"
      className="ml-1 flex-shrink-0 text-gray-400 hover:text-gray-500 focus:outline-none"
      onClick={onRemove}
    >
      <svg
        className="h-2 w-2"
        stroke="currentColor"
        fill="none"
        viewBox="0 0 8 8"
      >
        <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
      </svg>
    </button>
  </span>
);

const EditContexts = ({ contexts, onChange }) => {
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryContent, setNewCategoryContent] = useState("");
  const [newCategoryTriggers, setNewCategoryTriggers] = useState(""); // Use comma-separated string
  const [triggerInput, setTriggerInput] = useState({}); // State for individual category trigger inputs
  const [expandedCategoryName, setExpandedCategoryName] = useState(null); // State for accordion

  const handleSummaryChange = useCallback(
    (e) => {
      onChange({ ...contexts, summary: e.target.value });
    },
    [contexts, onChange]
  );

  const handleCategoryFieldChange = useCallback(
    (categoryName, field, value) => {
      const updatedCategories = {
        ...(contexts?.categories || {}),
        [categoryName]: {
          ...(contexts?.categories?.[categoryName] || {}),
          [field]: value,
        },
      };
      onChange({ ...(contexts || {}), categories: updatedCategories });
    },
    [contexts, onChange]
  );

  const handleAddTrigger = useCallback(
    (categoryName, trigger) => {
      if (!trigger || !trigger.trim()) return;
      const trimmedTrigger = trigger.trim();
      const currentTriggers =
        contexts?.categories?.[categoryName]?.triggers || [];
      if (!currentTriggers.includes(trimmedTrigger)) {
        handleCategoryFieldChange(categoryName, "triggers", [
          ...currentTriggers,
          trimmedTrigger,
        ]);
      }
    },
    [contexts, handleCategoryFieldChange]
  );

  const handleRemoveTrigger = useCallback(
    (categoryName, triggerToRemove) => {
      const currentTriggers =
        contexts?.categories?.[categoryName]?.triggers || [];
      handleCategoryFieldChange(
        categoryName,
        "triggers",
        currentTriggers.filter((t) => t !== triggerToRemove)
      );
    },
    [contexts, handleCategoryFieldChange]
  );

  const handleTriggerInputChange = (categoryName, value) => {
    setTriggerInput((prev) => ({ ...prev, [categoryName]: value }));
  };

  const handleTriggerInputKeyDown = (e, categoryName) => {
    if (e.key === "Enter" && triggerInput[categoryName]) {
      e.preventDefault();
      handleAddTrigger(categoryName, triggerInput[categoryName]);
      setTriggerInput((prev) => ({ ...prev, [categoryName]: "" })); // Clear input after adding
    }
  };

  const handleAddCategory = useCallback(() => {
    const trimmedName = newCategoryName.trim();
    if (!trimmedName || contexts?.categories?.[trimmedName]) {
      alert("Please enter a unique category name.");
      return;
    }
    const triggersArray = newCategoryTriggers
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t);

    const updatedCategories = {
      ...(contexts?.categories || {}),
      [trimmedName]: {
        content: newCategoryContent.trim(),
        triggers: triggersArray,
      },
    };
    onChange({
      ...(contexts || { summary: "" }),
      categories: updatedCategories,
    });

    // Reset add category form
    setNewCategoryName("");
    setNewCategoryContent("");
    setNewCategoryTriggers("");
  }, [
    newCategoryName,
    newCategoryContent,
    newCategoryTriggers,
    contexts,
    onChange,
  ]);

  const handleRemoveCategory = useCallback(
    (categoryName) => {
      const { [categoryName]: _, ...remainingCategories } =
        contexts?.categories || {};
      onChange({ ...contexts, categories: remainingCategories });
    },
    [contexts, onChange]
  );

  // Function to toggle category expansion
  const toggleCategoryExpansion = (name) => {
    setExpandedCategoryName(expandedCategoryName === name ? null : name);
  };

  const currentCategories = contexts?.categories
    ? Object.entries(contexts.categories)
    : [];

  return (
    <div className="mt-12">
      <h2 className="text-xl font-semibold text-slate-700 mb-2">Contexts</h2>
      <p className="text-gray-500 text-sm mb-4">
        Define context snippets that the AI can pull into the conversation based
        on trigger keywords.
      </p>
      <div className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white">
        <div className="p-6">
          {/* Summary Section */}
          <div>
            <h2 className="text-lg font-semibold leading-6 text-gray-900">
              Context Summary
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Base overview providing general context for the AI.
            </p>
            <textarea
              rows={3}
              className="mt-2 block w-full rounded-md border border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm p-2"
              placeholder="Enter context summary..."
              value={contexts?.summary || ""}
              onChange={handleSummaryChange}
            />
          </div>

          {/* Categories Section */}
          <div className="pt-6">
            <h2 className="text-lg font-semibold leading-6 text-gray-900">
              Context Categories
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Define specific context snippets triggered by keywords.
            </p>
            {currentCategories.length === 0 && (
              <p className="mt-4 text-sm text-gray-500 italic">
                No categories defined yet.
              </p>
            )}
            <div className="mt-4 space-y-2">
              {" "}
              {/* Reduced space-y for tighter list */}
              {currentCategories.map(([name, category]) => {
                const isExpanded = expandedCategoryName === name;
                return (
                  <div
                    key={name}
                    className="border border-gray-200 rounded-md overflow-hidden"
                  >
                    {/* Clickable Header */}
                    <button
                      type="button"
                      className="w-full flex justify-between items-center p-3 text-left bg-gray-50 hover:bg-gray-100 focus:outline-none"
                      onClick={() => toggleCategoryExpansion(name)}
                    >
                      <h3 className="text-md font-medium mb-0 text-gray-800">
                        {name}
                      </h3>
                      <span className="text-xs text-gray-500">
                        {isExpanded ? "Hide Details" : "Show Details"}
                        {/* Optional: Add an icon here like chevron up/down */}
                      </span>
                    </button>

                    {/* Collapsible Content Area */}
                    {isExpanded && (
                      <div className="p-4 border-t border-gray-200 bg-white">
                        <div className="flex justify-end mb-2">
                          {" "}
                          {/* Moved remove button inside */}
                          <button
                            onClick={() => handleRemoveCategory(name)}
                            className="text-red-600 hover:text-red-800 text-xs font-medium"
                          >
                            Remove Category
                          </button>
                        </div>
                        {/* Content */}
                        <label
                          htmlFor={`content-${name}`}
                          className="block text-sm font-medium text-gray-700"
                        >
                          Content
                        </label>
                        <textarea
                          id={`content-${name}`}
                          rows={4}
                          className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm p-2"
                          placeholder="Enter category content..."
                          value={category.content || ""}
                          onChange={(e) =>
                            handleCategoryFieldChange(
                              name,
                              "content",
                              e.target.value
                            )
                          }
                        />
                        {/* Triggers */}
                        <div className="mt-3">
                          <label
                            htmlFor={`triggers-${name}`}
                            className="block text-sm font-medium text-gray-700"
                          >
                            Triggers
                          </label>
                          <p className="text-xs text-gray-500">
                            Keywords that make this content available. Press
                            Enter to add.
                          </p>
                          <div className="flex flex-wrap items-start">
                            {(category.triggers || []).map((trigger) => (
                              <TriggerTag
                                key={trigger}
                                trigger={trigger}
                                onRemove={() =>
                                  handleRemoveTrigger(name, trigger)
                                }
                              />
                            ))}
                          </div>
                          <input
                            type="text"
                            id={`triggers-${name}`}
                            className="mt-2 block w-full rounded-md border border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm p-2"
                            placeholder="+ Add trigger and press Enter..."
                            value={triggerInput[name] || ""}
                            onChange={(e) =>
                              handleTriggerInputChange(name, e.target.value)
                            }
                            onKeyDown={(e) =>
                              handleTriggerInputKeyDown(e, name)
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Add New Category Section */}
          <div className="pt-6 border-t border-gray-200 mt-6">
            <h2 className="text-lg font-semibold leading-6 text-gray-900">
              Add New Category
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-y-4">
              <div>
                <label
                  htmlFor="new-category-name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Category Name
                </label>
                <input
                  type="text"
                  id="new-category-name"
                  placeholder="Unique category name..."
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm p-2"
                />
              </div>
              <div>
                <label
                  htmlFor="new-category-content"
                  className="block text-sm font-medium text-gray-700"
                >
                  Content
                </label>
                <textarea
                  id="new-category-content"
                  placeholder="Category content..."
                  value={newCategoryContent}
                  onChange={(e) => setNewCategoryContent(e.target.value)}
                  rows={3}
                  className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm p-2"
                />
              </div>
              <div>
                <label
                  htmlFor="new-category-triggers"
                  className="block text-sm font-medium text-gray-700"
                >
                  Triggers
                </label>
                <p className="text-xs text-gray-500">
                  Enter keywords separated by commas.
                </p>
                <input
                  type="text"
                  id="new-category-triggers"
                  placeholder="trigger1, another trigger, keyword3..."
                  value={newCategoryTriggers}
                  onChange={(e) => setNewCategoryTriggers(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm p-2"
                />
              </div>
              <div>
                <button
                  type="button"
                  onClick={handleAddCategory}
                  className="inline-flex justify-center rounded-md border border-transparent bg-black px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  + Add Category
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditContexts;
